/**
 * Support units of a brigade by brigade type (R1-03, R1-04; 2026-10-21, Dr. Tatar).
 *
 * Before: every person of the brigade who is not in a Tab 1 battalion (1,300 to 2,700 people) was ONE unit, 'Brigade HQ and
 * support', at the brigade position, with the sustainment modifier. Now that remainder is split into the support units the
 * brigade type really has, each with its own unit type (casualty modifier), its own position from the front, and its own
 * rear distance factor.
 *
 * WHICH units a type has: SOURCED (docs/research/RESEARCH_BRIGADE_SUPPORT.md section 3: US Army BCT organization, CRS IF10571,
 * ATP 4-90). Every maneuver BCT has a headquarters company, a signal company, a military intelligence company and a brigade
 * support battalion (which holds the medical company, distribution and maintenance companies). Fires and aviation brigades
 * carry headquarters, signal and a support battalion.
 * HOW BIG each is: no public source gives headcounts (unit tables are not public). The shares of the remainder below are
 * DERIVED from the support units listed in the tool's own doctrinal templates (unitTypes.js: infantry brigade 700 / 100 / 100 /
 * 200, artillery brigade 769 / 192 / 191, aviation brigade 926 / 371) and are EDUCATED GUESSES until a real table is supplied.
 * Combat support that is a battalion in Tab 1 (artillery, engineers, reconnaissance) is NOT repeated here.
 * The reconnaissance / cavalry unit (front unit) is added for maneuver brigades that have no reconnaissance battalion in Tab 1,
 * with the size of the nation. Not built here: the support structure of CSS brigades (sustainment, logistics, medical), which
 * are built from battalions only.
 */

import { SUPPORT_UNITS_BY_ECHELON } from './nationalStandards';

// Approved by Dr. Tatar 2026-10-21 after the before / after numbers (docs/TODO_CURRENT.md).
export const BRIGADE_SUPPORT_UNITS_ON = true;
// Reconnaissance / cavalry unit of the brigade, front unit, size by national standard (SUPPORT_UNITS_BY_ECHELON); approved 2026-10-21.
export const BRIGADE_RECON_UNIT_ON = true;

// Brigade support area: 10 to 30 km from the front line (US Army, qualitative, see rearAreaDecay.js); the middle is used.
export const BRIGADE_SUPPORT_AREA_KM = 20;

/**
 * Where a card that is anchored to its brigade stands (2026-10-25, Dr. Tatar: the brigade HQ distance moved no casualty number once the cards
 * were placed at build). 'BRIGADE' = at the brigade headquarters; 'SUPPORT_AREA' = the brigade support area, never closer to the front than
 * the brigade. Any other anchor (or none) = the card keeps its own position.
 * @returns {number|null} km from the FEBA, or null when the card is not anchored
 */
export function anchoredPositionKm(anchor, brigadePositionKm) {
  if (typeof brigadePositionKm !== 'number' || Number.isNaN(brigadePositionKm)) return null;
  if (anchor === 'BRIGADE') return brigadePositionKm;
  if (anchor === 'SUPPORT_AREA') return Math.max(BRIGADE_SUPPORT_AREA_KM, brigadePositionKm);
  return null;
}

/** The cards of one brigade with the anchored ones moved to the brigade position (the others are returned unchanged). */
export function moveAnchoredCards(battalions, brigadeId, brigadePositionKm) {
  return (battalions || []).map((bn) => {
    if (bn.parent_brigade_id !== brigadeId) return bn;
    const km = anchoredPositionKm(bn.position_anchor, brigadePositionKm);
    return km === null || km === bn.position_feba_km ? bn : { ...bn, position_feba_km: km };
  });
}

// Reconnaissance unit of a maneuver brigade by national standard: the entry of SUPPORT_UNITS_BY_ECHELON[nation].brigade.
// It differs by nation (RO company 80, DE company 110, FR squadron 110, PL company 100, NATO squadron 120, UK regiment 380,
// US cavalry squadron 400). Sizes in that table have NO recorded source (EDUCATED GUESS); that the unit exists is sourced for
// the US (cavalry squadron in every BCT, CRS IF10571) and Poland (17th Mechanized Brigade has a reconnaissance company).
// A brigade of the MULTINATIONAL standard has no entry: no reconnaissance unit is added (shown, not guessed).
// The UK moved its armoured cavalry (Household Cavalry, Royal Lancers) into the Deep Recce Strike brigade in 2022 (Janes), so
// the UK entry may be too high for a UK heavy brigade: to be checked.
export const BRIGADE_RECON_KEY_BY_STANDARD = Object.freeze({
  ROMANIAN: 'reconnaissance_company',
  NATO: 'reconnaissance_squadron',
  US: 'cavalry_squadron',
  GERMAN: 'aufklaerungskompanie',
  BRITISH: 'formation_recce_regiment',
  POLISH: 'szwadron_rozpoznawczy',
  FRENCH: 'escadron_eclairage_investigation'
});
const RECON_BRIGADE_TYPES = ['MECHANIZED', 'ARMOR', 'INFANTRY', 'AIRBORNE', 'AIR_ASSAULT'];   // Stryker already has a reconnaissance battalion in Tab 1

export const SUPPORT_STRUCTURE_STATUS = {
  composition: 'SOURCED',
  sizes: 'EDUCATED_GUESS',
  basis: 'Unit sizes: shares of the support units listed in the tool templates; no public headcounts (docs/research/RESEARCH_BRIGADE_SUPPORT.md).'
};

const MANEUVER_BCT_UNITS = [
  { key: 'hhc',    name: 'Headquarters company', unit_type: 'HQ',                    share: 100 / 1100, at: 'BRIGADE' },
  { key: 'signal', name: 'Signal company',       unit_type: 'SIGNAL',                share: 100 / 1100, at: 'BRIGADE' },
  { key: 'mi',     name: 'Military intelligence company', unit_type: 'MILITARY_INTELLIGENCE', share: 200 / 1100, at: 'BRIGADE' },
  { key: 'bsb',    name: 'Brigade support battalion (distribution, maintenance, medical companies)', unit_type: 'LOGISTICS', share: 700 / 1100, at: 'SUPPORT_AREA' }
];

export const BRIGADE_SUPPORT_UNITS = Object.freeze({
  MECHANIZED:  MANEUVER_BCT_UNITS,
  ARMOR:       MANEUVER_BCT_UNITS,
  INFANTRY:    MANEUVER_BCT_UNITS,
  STRYKER:     MANEUVER_BCT_UNITS,
  AIRBORNE:    MANEUVER_BCT_UNITS,
  AIR_ASSAULT: MANEUVER_BCT_UNITS,
  ARTILLERY: [
    { key: 'hq',     name: 'Headquarters battery', unit_type: 'HQ',        share: 192 / 1152, at: 'BRIGADE' },
    { key: 'signal', name: 'Signal company',       unit_type: 'SIGNAL',    share: 191 / 1152, at: 'BRIGADE' },
    { key: 'bsb',    name: 'Support battalion',    unit_type: 'LOGISTICS', share: 769 / 1152, at: 'SUPPORT_AREA' }
  ],
  AVIATION: [
    { key: 'hq',     name: 'Headquarters company', unit_type: 'HQ',          share: 371 / 1297, at: 'BRIGADE' },
    { key: 'bsb',    name: 'Aviation support battalion', unit_type: 'LOGISTICS', share: 926 / 1297, at: 'SUPPORT_AREA' }
  ]
});

/**
 * Split the remainder of a brigade (people not in a Tab 1 battalion) into its support units.
 * Returns null when the brigade type has no table (the caller keeps the single remainder unit).
 * Strengths are whole numbers that add up exactly to the remainder.
 */
export function splitBrigadeRemainder({ brigadeType, brigadeId, remainder, brigadePositionKm, nationalStandard, hasReconBattalion = false }) {
  const table = BRIGADE_SUPPORT_UNITS[brigadeType];
  if (!table) return null;
  if (typeof remainder !== 'number' || Number.isNaN(remainder) || remainder < 0) {
    throw new Error(`brigadeSupportStructure: remainder must be a number >= 0 (got: ${remainder})`);
  }
  if (typeof brigadePositionKm !== 'number' || Number.isNaN(brigadePositionKm)) {
    throw new Error(`brigadeSupportStructure: brigade ${brigadeId} has no position from the front line`);
  }
  const units = [];
  let left = remainder;
  const reconKey = BRIGADE_RECON_KEY_BY_STANDARD[nationalStandard];
  if (BRIGADE_RECON_UNIT_ON && reconKey && !hasReconBattalion && RECON_BRIGADE_TYPES.includes(brigadeType)) {
    const entry = SUPPORT_UNITS_BY_ECHELON[nationalStandard]?.brigade?.[reconKey];
    if (!entry || typeof entry.strength !== 'number') {
      throw new Error(`brigadeSupportStructure: no reconnaissance entry '${reconKey}' for national standard ${nationalStandard}`);
    }
    const strength = Math.min(entry.strength, left);   // a small brigade cannot hold more than its remainder
    units.push({
      id: `${brigadeId || 'brigade'}_recon`,
      name: `Reconnaissance / cavalry unit (${nationalStandard})`,
      type: 'BATTALION',
      unit_type: 'RECONNAISSANCE',
      strength,
      position_feba_km: brigadePositionKm
    });
    left -= strength;
  }
  let assigned = 0;
  units.push(...table.map((u, i) => {
    const strength = i === table.length - 1 ? left - assigned : Math.round(left * u.share);
    assigned += strength;
    return {
      id: `${brigadeId || 'brigade'}_${u.key}`,
      name: u.name,
      type: 'BATTALION',
      unit_type: u.unit_type,
      strength,
      position_feba_km: u.at === 'SUPPORT_AREA' ? Math.max(brigadePositionKm, BRIGADE_SUPPORT_AREA_KM) : brigadePositionKm
    };
  }));
  return units;
}
